import React from 'react';

import styled, { useTheme } from 'styled-components';

import Flex from '@components/layout/flex';
import Pill from '@components/basics/pill';
import Grid from '@components/basics/grid';
import Typography from '@components/basics/typography';
import AvatarPreview from '@components/layout/preview-box/avatar-preview';
import { ReleaseStatus } from '@common/types/release/relase-status';
import { getTrackCollaborators } from '@webapp/utils/tracks/get-track-collaborators';
import CatalogTrack from '@common/types/catalog/catalog-track';

interface DesktopTrackRowProps {
  track: CatalogTrack;
  releaseStatus: ReleaseStatus;
  isReleases: boolean;
  isArtist?: boolean;
  artistId?: string;
}

export default function DesktopTrackRow({
  track,
  releaseStatus,
  // isReleases,
  isArtist,
  artistId,
}: DesktopTrackRowProps) {
  const theme = useTheme();
  const { trackArtists, trackCredits } = React.useMemo(() => {
    const { collaborators, contributors } = getTrackCollaborators(
      track,
      artistId
    );
    const trackArtists =
      releaseStatus !== 'draft'
        ? collaborators.map(({ name, avatar }) => ({
            name,
            avatar,
          }))
        : [];
    const trackCredits =
      releaseStatus !== 'draft'
        ? contributors.map(({ name, avatar }) => ({
            name,
            avatar,
          }))
        : [];
    return { trackArtists, trackCredits };
  }, [track, artistId, releaseStatus]);

  return (
    <Wrapper>
      <SongName
        className="song-name"
        justifyContent="space-between"
        alignItems="center"
        gap={10}
      >
        <Flex alignItems="center" gap={10}>
          <SongNumber
            className={track.isNFT ? 'nft' : ''}
            justifyContent="center"
            alignItems="center"
          >
            <Typography className="number" type="small" weight="700">
              {track.order}
            </Typography>
          </SongNumber>
          <Grid gap={3}>
            <Typography
              className={track.isNFT ? 'nft' : ''}
              customFont="semi-bold"
            >
              {track.name}
            </Typography>
            <Typography type="small" color={theme.colors.tertiary}>
              {track.isrc ? track.isrc : '-'}
            </Typography>
          </Grid>
        </Flex>
        {track.isNFT && <Pill>NFT</Pill>}
      </SongName>

      <div className="third-column">
        {isArtist ? (
          <AvatarPreview
            items={trackArtists}
            avatarSize="24"
            breakingIndex={6}
            isTooltipEnabled
          />
        ) : (
          // TODO: DEVTEAM-603 - This needs to be integrated with the Track Ownership
          <div>
            <Typography type="small">M: 100%</Typography>
            <Typography type="small">P: 100%</Typography>
          </div>
        )}
      </div>
      <div className="fourth-column">
        {isArtist ? (
          <AvatarPreview
            items={trackCredits}
            avatarSize="24"
            breakingIndex={6}
            isTooltipEnabled
            noAvatar
          />
        ) : (
          // TODO: DEVTEAM-603 - This needs to be integrated with the Track Stakeholders
          <div>
            <Typography type="small">M: 3</Typography>
            <Typography type="small">P: 6</Typography>
          </div>
        )}
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: grid;
  grid-gap: 24px;
  grid-template-columns: repeat(18, minmax(0, 1fr));
  align-items: center;

  .avatar {
    grid-column: 1 / 2;
  }

  .song-name {
    grid-column: 2 / 8;
  }

  .third-column {
    grid-column: 8 / 11;
  }

  .fourth-column {
    grid-column: 11 / 15;
  }
`;

export const SongName = styled(Flex)`
  .nft {
    color: ${({ theme }) => theme.colors.tertiary};
  }
`;

export const SongNumber = styled(Flex)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  margin-right: 4px;

  .number {
    color: ${({ theme }) => theme.colors.differentBackground};
  }

  &.nft {
    background-color: ${({ theme }) => theme.colors.secondary};

    .number {
      color: ${({ theme }) => theme.colors.tertiary};
    }
  }
`;
